import * as signalR from '@microsoft/signalr';

// Build the base URL dynamically to support sub-applications (e.g., /agatrsstage/)
const _host = window.location.host;
const _base = process.env.BASE_URL || '/';
// Make sure base URL ends with a slash for proper path concatenation
const baseUrl = window.location.protocol + '//' + _host + (_base.endsWith('/') ? _base : _base + '/');

class AuditLogSignalRService {
  constructor() {
    this.connection = null;
    this.orderCallbacks = [];
    this.userCallbacks = [];
    this.isConnected = false;
  }

  /**
   * Initialize the SignalR connection
   */
  async connect() {
    if (this.connection && this.isConnected) {
      return;
    }

    const hubUrl = baseUrl + 'hubs/auditlog';

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        // Cookies are automatically sent with the connection
        withCredentials: true
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    // Handle order audit log updates
    this.connection.on('OrderAuditLogCreated', (data) => {
      this.orderCallbacks.forEach(callback => callback(data));
    });

    // Handle user audit log updates
    this.connection.on('UserAuditLogCreated', (data) => {
      this.userCallbacks.forEach(callback => callback(data));
    });

    // Handle connection state changes
    this.connection.onclose(() => {
      this.isConnected = false;
    });

    this.connection.onreconnecting(() => {
      this.isConnected = false;
    });

    this.connection.onreconnected(() => {
      this.isConnected = true;
    });

    try {
      await this.connection.start();
      this.isConnected = true;
    } catch (err) {
      console.error('SignalR connection error:', err);
      this.isConnected = false;
    }
  }

  /**
   * Disconnect from SignalR
   */
  async disconnect() {
    if (this.connection) {
      await this.connection.stop();
      this.isConnected = false;
    }
  }

  /**
   * Subscribe to order audit log updates
   * @param {number} orderId - The order ID to subscribe to
   * @param {function} callback - Callback function to handle updates
   */
  async subscribeToOrder(orderId, callback) {
    if (!this.isConnected) {
      await this.connect();
    }

    if (callback && !this.orderCallbacks.includes(callback)) {
      this.orderCallbacks.push(callback);
    }

    try {
      await this.connection.invoke('SubscribeToOrder', orderId);
    } catch (err) {
      console.error('Failed to subscribe to order:', err);
    }
  }

  /**
   * Unsubscribe from order audit log updates
   * @param {number} orderId - The order ID to unsubscribe from
   * @param {function} callback - Callback function to remove
   */
  async unsubscribeFromOrder(orderId, callback) {
    if (callback) {
      this.orderCallbacks = this.orderCallbacks.filter(cb => cb !== callback);
    }

    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke('UnsubscribeFromOrder', orderId);
      } catch (err) {
        console.error('Failed to unsubscribe from order:', err);
      }
    }
  }

  /**
   * Subscribe to user audit log updates
   * @param {string} userId - The user ID to subscribe to
   * @param {function} callback - Callback function to handle updates
   */
  async subscribeToUser(userId, callback) {
    if (!this.isConnected) {
      await this.connect();
    }

    if (callback && !this.userCallbacks.includes(callback)) {
      this.userCallbacks.push(callback);
    }

    try {
      await this.connection.invoke('SubscribeToUser', userId);
    } catch (err) {
      console.error('Failed to subscribe to user:', err);
    }
  }

  /**
   * Unsubscribe from user audit log updates
   * @param {string} userId - The user ID to unsubscribe from
   * @param {function} callback - Callback function to remove
   */
  async unsubscribeFromUser(userId, callback) {
    if (callback) {
      this.userCallbacks = this.userCallbacks.filter(cb => cb !== callback);
    }

    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke('UnsubscribeFromUser', userId);
      } catch (err) {
        console.error('Failed to unsubscribe from user:', err);
      }
    }
  }
}

export default new AuditLogSignalRService();
